import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { Table as ReactTableType } from "@tanstack/react-table";
import { PiMagnifyingGlassBold } from "react-icons/pi";
import { Badge, Button, Flex, Input, Text, type SelectOption } from "rizzui";
import { cn, getStatusBadge, StatusField, ToggleColumns } from "@steelyard/theme";

/**
 * The one list toolbar: status dropdown, search, the module's own filters, then Reset, the column
 * toggle and the screen's own actions on the right. Controls COPIED from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/shipment/list/filters.tsx (the search
 * `Input`, `StatusField`, `ToggleColumns`) with the status `StatusField` placed first, the way
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/users-table/filters.tsx places its own.
 *
 * Laid out as the Marketplace admin portal's `GridToolbar` (2026-09-28, the project owner's reference
 * for every back-office grid): filters sit flush on the page above the table rather than inside its
 * card, each with its label above the control ("Status", "Search", …), in a responsive grid — one
 * column on a phone, two on a tablet, one wrapping row on a wide screen — with Reset and the page's
 * actions right-aligned at the end of that row. That replaced the template's inline-or-drawer split:
 * every field is always inline, so there is one copy of each control, never two.
 *
 * Two differences from the template, both because every list here filters on the SERVER:
 *
 *  - the status control navigates. Each option carries an `href` built with `hrefWith`, so choosing
 *    one keeps the page size and every other filter, and the loader refetches. An option's count is
 *    whatever the screen already had — the API's own number, or absent — never counted here.
 *  - Reset is a navigation too (to the screen's own unfiltered URL), and it also drops a
 *    `table.setGlobalFilter` search, which lives in the table rather than the URL.
 */

/** A toolbar field's label: small and muted, above its control — pass as a rizzui `labelClassName`. */
export const FILTER_LABEL = "mb-1 block text-xs font-medium text-gray-500";

export type StatusOption = {
  label: string;
  /** Where choosing this option goes — `hrefWith(path, searchParams, { status, page: undefined })`. */
  href: string;
  active: boolean;
  /** The API's own count for this option, or absent. Never derived. */
  count?: number | null;
  /** A status key for `getStatusBadge`'s dot, so the option reads like the rows' own badge. */
  status?: string;
};

export type StatusFilterProps = {
  label: string;
  options: StatusOption[];
  /** What the closed control reads when no option is active (a screen with no "All" option). */
  placeholder?: string;
};

/**
 * All / Active / Inactive for any list of active-flagged records (brands, categories, products,
 * tiers). `href` builds each view's URL (`hrefWith` + `activeViewParams`); a count the screen has
 * no API figure for stays absent rather than being worked out as "all − active".
 */
export function activeStatusOptions(
  view: "all" | "active" | "inactive",
  href: (view: "all" | "active" | "inactive") => string,
  counts: { all?: number | null; active?: number | null; inactive?: number | null },
): StatusOption[] {
  return [
    { label: "All", href: href("all"), active: view === "all", count: counts.all },
    { label: "Active", href: href("active"), active: view === "active", count: counts.active, status: "active" },
    { label: "Inactive", href: href("inactive"), active: view === "inactive", count: counts.inactive, status: "inactive" },
  ];
}

type Option = StatusOption & SelectOption;

/** An option in the open list: its label (as the rows' own badge, when it names a status) and its count. */
function OptionDisplay({ option, withCount = true }: { option: StatusOption; withCount?: boolean }) {
  return (
    <Flex align="center" justify="between" gap="3" className="w-full">
      {option.status ? getStatusBadge(option.status) : <Text className="font-medium">{option.label}</Text>}
      {!withCount || option.count === undefined || option.count === null ? null : (
        <Badge size="sm" variant="flat" rounded="md" className="tabular-nums">
          {option.count}
        </Badge>
      )}
    </Flex>
  );
}

/** The status filter as a dropdown — the template's `StatusField` (rizzui `Select`). */
export function StatusFilter({ label, options, placeholder, onSelect }: StatusFilterProps & { onSelect?: () => void }) {
  const navigate = useNavigate();
  const selected = options.find((option) => option.active) ?? (placeholder ? undefined : options[0]);
  return (
    <StatusField
      label={label}
      labelClassName={FILTER_LABEL}
      placeholder={placeholder}
      options={options.map((option) => ({ ...option, value: option.href }))}
      value={selected?.href}
      onChange={(href: string) => {
        onSelect?.();
        navigate(href);
      }}
      getOptionValue={(option) => option.value}
      getOptionDisplayValue={(option) => <OptionDisplay option={option as Option} />}
      // Closed, it names the choice only: the selected option's count is the list's own total,
      // which the page header already shows — the same figure twice on one screen is noise.
      displayValue={() => (selected ? <OptionDisplay option={selected} withCount={false} /> : placeholder)}
      // In a portal, so the open list layers above the table below it rather than under its rows.
      inPortal
      // rizzui pins the list to the trigger's width, so a long state ("Awaiting Company Approval")
      // wrapped inside the fixed-height row and overlapped the next one. Grow to the widest label.
      dropdownClassName="h-auto !w-max min-w-[var(--button-width)]"
      // rizzui wraps each option's display in an auto-width div; stretching it right-aligns the count.
      optionClassName="h-9 whitespace-nowrap [&>div]:w-full"
      selectClassName="min-w-[200px]"
      className="w-full @5xl:w-auto"
    />
  );
}

/**
 * The template's search box bound to `table.setGlobalFilter` — it searches the rows already loaded:
 * every row of a whole-set list, or one page of a server-paged one, which its placeholder must then
 * say ("Search this page…"). A search the API can run goes through `SearchInput` instead.
 */
export function TableSearch<T extends Record<string, unknown>>({ table, placeholder }: { table: ReactTableType<T>; placeholder: string }) {
  return (
    <Input
      type="search"
      label="Search"
      labelClassName={FILTER_LABEL}
      placeholder={placeholder}
      value={table.getState().globalFilter ?? ""}
      onClear={() => table.setGlobalFilter("")}
      onChange={(event) => {
        table.setGlobalFilter(event.target.value);
        // A list paged here shows its matches from the first page; a server page stays where it is.
        if (!table.options.manualPagination) table.setPageIndex(0);
      }}
      className="w-full @5xl:w-64"
      clearable
      prefix={<PiMagnifyingGlassBold className="size-4" />}
    />
  );
}

export function TableToolbar<T extends Record<string, unknown>>({
  table,
  status,
  search,
  filters,
  clear,
  actions,
  inset = false,
}: {
  table: ReactTableType<T>;
  status?: StatusFilterProps;
  search?: ReactNode;
  /** The module's own secondary fields, each a labelled control, laid out with the rest. */
  filters?: ReactNode;
  /** `active` is the screen's own "is anything filtered" rule; `href` is its unfiltered URL. */
  clear?: { active: boolean; href: string };
  /** The screen's own buttons (New …, links to sibling screens), right-aligned after Reset. */
  actions?: ReactNode;
  /** Inside a titled card rather than flush on the page: carries the card's gutter and divider. */
  inset?: boolean;
}) {
  const navigate = useNavigate();
  const searched = Boolean(table.getState().globalFilter);
  const canReset = Boolean(clear?.active) || searched;

  return (
    <div className={cn("@container", inset && "border-b border-muted px-4 py-4 lg:px-6")}>
      <div className="grid grid-cols-1 items-end gap-3 @xl:grid-cols-2 @xl:gap-4 @5xl:flex @5xl:flex-wrap">
        {/* A different status is a different result set; a search typed against the old one would
            silently hide rows in it, so it goes too. */}
        {status ? <StatusFilter {...status} onSelect={() => table.resetGlobalFilter()} /> : null}
        {search}
        {filters}

        <Flex align="center" justify="end" gap="2" className="ms-auto w-auto shrink-0 flex-wrap">
          {clear || searched ? (
            <Button
              variant="flat"
              className="bg-gray-100 hover:bg-gray-200"
              disabled={!canReset}
              onClick={() => {
                table.resetGlobalFilter();
                if (clear?.active) navigate(clear.href);
              }}
            >
              Reset
            </Button>
          ) : null}
          <ToggleColumns table={table} />
          {actions}
        </Flex>
      </div>
    </div>
  );
}
